<template>
  <div class="chart">
    <canvas :id="id" class="chart-canvas" :height="height"></canvas>
  </div>
</template>
<script>
import Chart from "chart.js/auto";
import { markRaw } from "vue";

export default {
  name: "ReportsBarChart",
  props: {
    id: {
      type: String,
      default: "bar-chart",
    },
    height: {
      type: [Number, String],
      default: "170",
    },
    colors: {
      type: Array,
      default: () => [
        "rgba(236, 72, 153, 0.85)", // Pink
        "rgba(139, 92, 246, 0.85)", // Purple
        "rgba(59, 130, 246, 0.85)", // Blue
      ],
    },
  },

  data() {
    return {
      chartInstance: null,
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.initializeChart();
    });
  },

  beforeUnmount() {
    this.destroyChart();
  },

  methods: {
    initializeChart() {
      const canvas = document.getElementById(this.id);
      if (!canvas) {
        console.warn(`Canvas element with id "${this.id}" not found`);
        return;
      }

      const ctx = canvas.getContext("2d");

      // Destroy existing chart
      this.destroyChart();

      // Create chart and mark it as non-reactive
      const chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: [],
          datasets: [
            {
              label: "",
              tension: 0.4,
              borderWidth: 0,
              borderRadius: 4,
              borderSkipped: false,
              backgroundColor: this.colors,
              hoverBackgroundColor: this.colors.map((c) =>
                c.replace("0.85", "1")
              ),
              data: [],
              maxBarThickness: 40,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
                color: "rgba(0,0,0,0.08)",
              },
              ticks: {
                suggestedMin: 0,
                suggestedMax: 500,
                beginAtZero: true,
                padding: 10,
                font: {
                  size: 14,
                  weight: 300,
                  family: "Roboto",
                  style: "normal",
                  lineHeight: 2,
                },
                color: "#333",
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
                color: "rgba(0,0,0,0.05)",
              },
              ticks: {
                display: true,
                color: "#666",
                padding: 10,
                font: {
                  size: 14,
                  weight: 300,
                  family: "Roboto",
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
          },
        },
      });

      // Mark the chart instance as non-reactive
      this.chartInstance = markRaw(chart);
    },

    updateChart(chartData) {
      if (!chartData || !this.chartInstance) {
        console.warn("Cannot update chart - missing data or chart instance");
        return;
      }

      try {
        // Create completely plain objects with no Vue reactivity
        const plainLabels = chartData.labels ? chartData.labels.slice() : [];
        const plainData = chartData.datasets?.data
          ? chartData.datasets.data.slice()
          : [];
        const plainLabel = chartData.datasets?.label || "";

        console.log("Updating chart with plain data:", {
          plainLabels,
          plainData,
          plainLabel,
        });

        // Update chart data directly
        this.chartInstance.data.labels.splice(
          0,
          this.chartInstance.data.labels.length,
          ...plainLabels
        );
        this.chartInstance.data.datasets[0].data.splice(
          0,
          this.chartInstance.data.datasets[0].data.length,
          ...plainData
        );
        this.chartInstance.data.datasets[0].label = plainLabel;

        // Dynamically adjust y-axis max
        if (plainData.length > 0) {
          const maxValue = Math.max(...plainData);
          this.chartInstance.options.scales.y.ticks.suggestedMax = Math.ceil(
            maxValue * 1.2
          );
        }

        this.chartInstance.update("none"); // Update without animation
      } catch (error) {
        console.error("Error updating chart:", error);
      }
    },

    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
  },
};
</script>
